import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {BdMahnwesenQlService} from '../../../service/ql/bd-mahnwesen-ql.service';
import {BdMahnwesenKpiDto} from '../../../model/dto/api/bd-mahnwesen-kpi.dto';

/**
 * AI:
 * Status: 'wave-4-implementation'
 * Type: Component
 * SubType: Shared / KPI-Display
 * Reason: Zeigt die Mahnwesen-KPIs aus {@link BdMahnwesenQlService.getKpiAggregations()}
 *         im Mahnwesen-Übersichts-Tab an. Enthält:
 *           - Gesamtzahl aktiver Mahnungen
 *           - Anzahl pro Level (Kurzname)
 *           - Anzahl pro Pfad (DunningType-Name)
 *           - Anzahl offener Aktionen
 *           - Anzahl gebrochener Zahlungspläne
 *           - Anzahl ausstehender Freigaben (PENDING_APPROVAL)
 *           - Anzahl aktiver Pausen
 *
 *         Lädt Daten automatisch beim Init und bei Änderungen von billingAreaId
 *         (reserviert für zukünftige Scope-Erweiterungen).
 *
 *         Verwendet eine 7-Spalten-PrimeFlex-Kachelstruktur für das KPI-Dashboard.
 *
 * @ticket TM-1000001786
 */
@Component({
    selector: 'bd-mahnwesen-kpi',
    templateUrl: './bd-mahnwesen-kpi.component.html',
    standalone: false
})
export class BdMahnwesenKpiComponent implements OnInit, OnChanges, OnDestroy {

    // ------------------------------------------------------------------ //
    // Inputs
    // ------------------------------------------------------------------ //

    /**
     * Optionaler Filter auf Abrechnungsbereich — reserviert für zukünftige
     * Erweiterungen, aktuell nicht aktiv.
     */
    @Input() billingAreaId: number | null = null;

    // ------------------------------------------------------------------ //
    // State
    // ------------------------------------------------------------------ //

    initialized: boolean = false;
    loading: boolean = false;

    /** Geladene KPI-Daten. Null bis zur ersten Antwort. */
    kpi: BdMahnwesenKpiDto | null = null;

    /** Fehler-Flag: true, wenn die letzte Anfrage gescheitert ist. */
    hasError: boolean = false;

    private kpiSubscription: Subscription | null = null;

    constructor(private readonly mahnwesenQlService: BdMahnwesenQlService) {}

    // ------------------------------------------------------------------ //
    // Lifecycle
    // ------------------------------------------------------------------ //

    ngOnInit(): void {
        this.loadKpis();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['billingAreaId'] && !changes['billingAreaId'].firstChange) {
            if (this.initialized) {
                this.loadKpis();
            }
        }
    }

    ngOnDestroy(): void {
        this.kpiSubscription?.unsubscribe();
    }

    // ------------------------------------------------------------------ //
    // Data loading
    // ------------------------------------------------------------------ //

    /**
     * Lädt alle KPI-Aggregationen via {@link BdMahnwesenQlService.getKpiAggregations()}.
     */
    loadKpis(): void {
        this.kpiSubscription?.unsubscribe();
        this.loading = true;
        this.hasError = false;

        this.kpiSubscription = this.mahnwesenQlService
            .getKpiAggregations()
            .subscribe({
                next: (kpi: BdMahnwesenKpiDto) => {
                    this.kpi = kpi;
                    this.loading = false;
                },
                error: () => {
                    this.kpi = null;
                    this.hasError = true;
                    this.loading = false;
                }
            });
    }

    // ------------------------------------------------------------------ //
    // Template helpers
    // ------------------------------------------------------------------ //

    /**
     * Gibt einen sortierten Array aus Level-Name/Anzahl-Paaren zurück.
     * Sortierung: Level-Kurzname alphabetisch.
     */
    getLevelEntries(): {level: string; count: number}[] {
        if (!this.kpi?.perLevel) {
            return [];
        }
        return Object.entries(this.kpi.perLevel)
            .map(([level, count]) => ({level, count}))
            .sort((a, b) => a.level.localeCompare(b.level));
    }

    /**
     * Gibt einen sortierten Array aus Pfad-Name/Anzahl-Paaren zurück.
     * Sortierung: Pfad-Name alphabetisch.
     */
    getPfadEntries(): {pfad: string; count: number}[] {
        if (!this.kpi?.perPfad) {
            return [];
        }
        return Object.entries(this.kpi.perPfad)
            .map(([pfad, count]) => ({pfad, count}))
            .sort((a, b) => a.pfad.localeCompare(b.pfad));
    }
}
